// src/Steps/StepTwo.jsx

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import HCaptcha from "@hcaptcha/react-hcaptcha";
import "./css/stepTwo.css";
import formatDateToDutch from './Utils/formatDateToDutch';
import useFetchFields from './Hooks/useFetchFields';
import useTurnstile from './Hooks/useTurnstile';
import moment from 'moment';
import 'moment/locale/nl';
import useApi from '../../../Hooks/useApi';
import sound1 from './sound1.mp3';
import buildValidationSchema from './Utils/validationSchema';
import useAvailableMenus from './Hooks/useAvailableMenus';
import useCaptchaFlow from './Hooks/useCaptchaFlow';
import useSubmission from './Hooks/useSubmission';

const StepTwo = ({
  title,
  formData,
  handleChange,
  setCurrentStep,
  menu = [],
  initialMenuTitle = '',
}) => {
  const { fields, loadingFields } = useFetchFields();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableMenus, setAvailableMenus] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Captcha states
  const [turnstileAttempted, setTurnstileAttempted] = useState(false);

  const { post } = useApi();
  const hcaptchaRef = useRef(null);

  // Initialize Turnstile hook
  const {
    isActive: turnstileActive,
    turnstileToken,
    turnstileLoading,
    turnstileError,
    executeTurnstile,
    resetTurnstile,
    hasValidToken: hasTurnstileToken
  } = useTurnstile(window.restaurantId);

  // hCaptcha site key
  const HCAPTCHA_SITE_KEY = '3d04a13e-6ec6-45f0-ad56-2104f9a87d76';

  const validationSchema = useMemo(() => buildValidationSchema(availableMenus), [availableMenus]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...formData,
      restaurantId: window.restaurantId || '',
      date: window.selectedDate,
      menu: '',
      giftcard: formData.giftcard || '',
    },
  });

  const watchedValues = watch();
  const formattedDate = formatDateToDutch(watchedValues.date);
  const availableMenusHook = useAvailableMenus(menu, watchedValues.date, watchedValues.time);
  useEffect(() => setAvailableMenus(availableMenusHook), [availableMenusHook]);

  // hCaptcha handlers
  const {
    showHCaptcha,
    setShowHCaptcha,
    hcaptchaToken,
    setHcaptchaToken,
    captchaError,
    handleHCaptchaVerify,
    handleHCaptchaExpire,
    handleHCaptchaError,
    resetAllCaptchas,
    attemptTurnstileVerification,
  } = useCaptchaFlow({
    turnstileActive,
    executeTurnstile,
    hasTurnstileToken,
    turnstileToken,
    resetTurnstile,
    hcaptchaRef,
  });

  // resetAllCaptchas is provided by useCaptchaFlow

  // Attempt Turnstile verification
  const onSubmit = useSubmission({
    post,
    initialMenuTitle,
    formData,
    turnstileToken,
    hasTurnstileToken,
    attemptTurnstileVerification,
    resetTurnstile,
    hcaptchaToken,
    setTurnstileAttempted,
    resetAllCaptchas,
    soundSrc: sound1,
    setCurrentStep,
    setIsSubmitting,
    setShowNotification,
    setErrorMessage,
  });

  // When email field loses focus and is valid, trigger Turnstile
  const handleEmailBlur = async () => {
    await trigger('email');
    const isEmailValid = !errors.email && watchedValues.email;
    if (isEmailValid && !turnstileAttempted) {
      console.log('Email filled/validated; running Turnstile early');
      setTurnstileAttempted(true);
      const token = await attemptTurnstileVerification();
      if (!token) {
        console.log('Turnstile early attempt failed; will fall back to hCaptcha on submit');
      }
    }
  };


  const onError = (errors) => {
    console.error('Form submission errors:', errors);
  };

  // Handle hCaptcha completion and retry submission once
  useEffect(() => {
    if (hcaptchaToken && showHCaptcha && !isSubmitting) {
      console.log('hCaptcha completed, retrying submission');
      setShowHCaptcha(false);
      // Trigger form submission again
      handleSubmit(onSubmit)();
    }
  }, [hcaptchaToken, showHCaptcha, isSubmitting]);

  if (loadingFields) {
    return <div>Loading fields...</div>;
  }

  // Determine if this is a waitlist reservation to adapt button text
  const isWaitlistReservation =
    watchedValues.waitlist === true || watchedValues.waitlist === 'true';

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <h2 className="title default-text-color">{title}</h2>
      <h3 className="subtitle default-text-color">Stap 2/3</h3>

      {/* Error notifications */}
      {showNotification && (
        <div className="alert-warning">
          {errorMessage}
        </div>
      )}

      {/* Captcha error notification */}
      {captchaError && (
        <div className="alert-warning">
          {captchaError}
        </div>
      )}

      {/* Selection bubbles */}
      <div className="flex-container">
        <div className="bubble-style" onClick={() => setCurrentStep(1, 'guest')}>
          {watchedValues.guests} Gasten
        </div>
        <div className="bubble-style" onClick={() => setCurrentStep(1, 'date')}>
          {formattedDate}
        </div>
        <div className="bubble-style" onClick={() => setCurrentStep(1, 'time')}>
          {watchedValues.time}
        </div>
        {watchedValues.giftcard && (
          <div
            className="bubble-style"
            onClick={() => setCurrentStep(1, 'giftcard')}
          >
            {watchedValues.giftcard}
          </div>
        )}
      </div>

      {/* Menu selection */}
      {availableMenus.length > 0 && (
        <div className="form-group">
          <label className="default-text-color" htmlFor="menu">
            Selecteer Menu<span className="required">*</span>
          </label>
          <select
            id="menu"
            {...register('menu')}
            className={`form-control form-menu ${errors.menu ? 'is-invalid' : ''}`}
            onBlur={() => trigger('menu')}
          >
            <option value="">-- Kies een menu --</option>
            {availableMenus.map((menuItem) => (
              <option key={menuItem._id.$oid || menuItem._id} value={menuItem.name}>
                {menuItem.name}
              </option>
            ))}
          </select>
          {errors.menu && (
            <span className="error-message">{errors.menu.message}</span>
          )}
        </div>
      )}

      {/* Form fields */}
      {fields.map((field) => (
        <div className="form-group" key={field.id}>
          <label className="default-text-color" htmlFor={field.id}>
            {field.label}
            {field.required && <span className="required">*</span>}
          </label>
          {field.id === 'date' ||
          field.id === 'time' ||
          field.id === 'guests' ||
          field.id === 'giftcard' ? null : field.type === 'textarea' ? (
            <textarea
              id={field.id}
              {...register(field.id)}
              placeholder={field.placeholder || ''}
              className={`form-control ${errors[field.id] ? 'is-invalid' : ''}`}
              onBlur={() => trigger(field.id)}
            ></textarea>
          ) : (
            <input
              type={
                field.id === 'email'
                  ? 'email'
                  : field.type === 'input'
                  ? 'text'
                  : field.type
              }
              id={field.id}
              {...register(field.id)}
              placeholder={field.placeholder || ''}
              className={`form-control ${errors[field.id] ? 'is-invalid' : ''}`}
              onBlur={
                field.id === 'email'
                  ? handleEmailBlur
                  : () => trigger(field.id)
              }
            />
          )}
          {errors[field.id] && (
            <span className="error-message">{errors[field.id].message}</span>
          )}
        </div>
      ))}

      {/* hCaptcha widget - only shown when needed */}
      {showHCaptcha && (
        <div className="form-group captcha-group">
          <label className="default-text-color">
            Veiligheidsverificatie<span className="required">*</span>
          </label>
          <div className="captcha-widget-container">
            <HCaptcha
              ref={hcaptchaRef}
              languageOverride="nl"
              sitekey={HCAPTCHA_SITE_KEY}
              onVerify={handleHCaptchaVerify}
              onExpire={handleHCaptchaExpire}
              onError={handleHCaptchaError}
              size="normal"
              theme="light"
            />
          </div>
        </div>
      )}

      {/* Turnstile loading indicator */}
      {turnstileLoading && !showHCaptcha && (
        <div className="turnstile-loading">
          Veiligheidsverificatie wordt geladen...
        </div>
      )}

      <button 
        type="submit" 
        className="submit-button" 
        disabled={isSubmitting || (showHCaptcha && !hcaptchaToken)}
      >
        {isSubmitting
          ? (isWaitlistReservation ? 'Inschrijven Wachtlijst...' : 'Reserveren...')
          : (isWaitlistReservation ? 'Inschrijven Wachtlijst' : 'Reserveren')}
      </button>
    </form>
  );
};

export default StepTwo;
